// ─────────────────────────────────────────────────────────────────────────
// IntegrationsSetupGuide.tsx — full step-by-step instructions for the
// customer's Azure admin to set up the Microsoft Graph app registration
// that CIMA will use for Teams.
//
// Opens in a new tab from Settings → Integrations. Long, scrollable,
// anchored sections so admins can deep-link mid-flow.
// ─────────────────────────────────────────────────────────────────────────

import React from 'react';

export function IntegrationsSetupGuide({ onBack }: { onBack: () => void }) {
  return (
    <div style={{
      maxWidth: 840, margin: '0 auto', padding: '32px 28px 80px',
      color: 'var(--fg)', lineHeight: 1.65, fontSize: 14,
    }}>
      <button onClick={onBack} style={{
        background: 'transparent', border: 'none', color: 'var(--muted)',
        cursor: 'pointer', padding: 0, marginBottom: 18, fontSize: 13,
      }}>← Back to Settings</button>

      <h1 style={{ fontSize: 28, fontWeight: 800, margin: '0 0 6px', letterSpacing: '-0.02em' }}>
        Microsoft Teams setup guide
      </h1>
      <p style={{ fontSize: 14, color: 'var(--muted)', margin: '0 0 24px' }}>
        Step-by-step instructions for your Azure admin. Takes ~15 minutes.
      </p>

      {/* Quick TOC */}
      <div style={tocStyle}>
        <strong style={{ display: 'block', marginBottom: 6, fontSize: 11, letterSpacing: 0.5, textTransform: 'uppercase', color: 'var(--muted)' }}>On this page</strong>
        <a href="#prereq"   style={tocLink}>1 · Before you start</a>
        <a href="#register" style={tocLink}>2 · Create an app registration</a>
        <a href="#ids"      style={tocLink}>3 · Copy the IDs</a>
        <a href="#secret"   style={tocLink}>4 · Create a client secret</a>
        <a href="#perms"    style={tocLink}>5 · Add API permissions + admin consent</a>
        <a href="#policy"   style={tocLink}>6 · Application Access Policy (PowerShell)</a>
        <a href="#premium"  style={tocLink}>7 · (Recommended) Teams Premium for transcripts</a>
        <a href="#paste"    style={tocLink}>8 · Paste credentials into CIMA</a>
        <a href="#verify"   style={tocLink}>9 · Read the verification result</a>
        <a href="#errors"   style={tocLink}>10 · Common errors and fixes</a>
      </div>

      <Section id="prereq" num="1" title="Before you start">
        <WhereBadge tool="checklist" />
        <p style={{ marginTop: 10, marginBottom: 10 }}>
          You'll touch four different tools during this setup. Keep them all open in separate browser tabs so you can switch back and forth:
        </p>
        <div style={{ background: 'var(--surface)', border: '1px solid var(--border-md)', borderRadius: 8, padding: '12px 14px', margin: '10px 0' }}>
          <ToolRow icon="🔵" name="Azure Portal" url="https://portal.azure.com" purpose="Where you create the app registration, secret, and grant permissions (steps 2-5)" />
          <ToolRow icon="🟢" name="Microsoft 365 Admin Center" url="https://admin.microsoft.com" purpose="Where you assign the Teams Premium licence (step 7)" />
          <ToolRow icon="⚫" name="PowerShell on your own laptop" url={null} purpose="Where you run the access-policy command (step 6) — not in any browser tab" />
          <ToolRow icon="🟣" name="CIMA" url="https://cima.enigmaglobal.ai" purpose="Where you paste the credentials at the end (step 8)" />
        </div>
        <p style={{ marginTop: 14, marginBottom: 6 }}><strong>Pre-flight checklist:</strong></p>
        <ul style={ulStyle}>
          <li>You are signed in as <strong>Global Administrator</strong> on the Azure Portal — without this you can't grant admin consent in step 5 and the whole setup fails.</li>
          <li>~15 minutes uninterrupted.</li>
          <li>A Microsoft 365 mailbox already exists for the "meeting organiser" — e.g. <code style={codeStyle}>cima-bot@yourcompany.com.au</code>. This is the account that appears as the meeting host on every CIMT calendar invite. <strong>If you haven't created this mailbox yet, do that first in Microsoft 365 Admin Center → Users → Add a user.</strong></li>
          <li>Recommended: a <strong>Teams Premium</strong> licence assigned to that organiser mailbox (you'll do this in step 7). Without it, CIMA can still schedule meetings but transcript-pulling degrades to reading meeting chat instead of the recorded transcript.</li>
        </ul>
      </Section>

      <Section id="register" num="2" title="Create an app registration (in Azure Portal)">
        <WhereBadge tool="azure" />
        <ol style={olStyle}>
          <li>In a new browser tab, go to <ExtLink href="https://portal.azure.com">portal.azure.com</ExtLink> and sign in as Global Admin.</li>
          <li>In the search bar at the top of the page, type <code style={codeStyle}>App registrations</code> and click the result with the same name. <em>This is an Azure-wide service — not buried in any sidebar.</em></li>
          <li>On the App registrations page, click the <strong>+ New registration</strong> button at the top of the page.</li>
          <li>On the "Register an application" form:
            <ul style={{ ...ulStyle, marginTop: 6 }}>
              <li><strong>Name</strong>: type <code style={codeStyle}>CIMA Crisis Management</code> (or your own naming convention).</li>
              <li><strong>Supported account types</strong>: select the FIRST radio button — "Accounts in this organizational directory only (Single tenant)".</li>
              <li><strong>Redirect URI</strong>: leave it completely blank. Don't change the dropdown. <em>CIMA uses client-credentials authentication, not the OAuth redirect flow.</em></li>
            </ul>
          </li>
          <li>Click the blue <strong>Register</strong> button at the bottom.</li>
          <li>You'll be taken to the new app's Overview page. <strong>Keep this tab open.</strong> Steps 3, 4, and 5 all happen here.</li>
        </ol>
      </Section>

      <Section id="ids" num="3" title="Copy the Tenant ID and App Client ID (in Azure Portal)">
        <WhereBadge tool="azure" />
        <p>You should now be looking at the new app's <strong>Overview</strong> page in the Azure Portal. (If you navigated away: search "App registrations" again, click the "Owned applications" tab, then click your CIMA app.)</p>
        <p>In the "Essentials" section at the top of the Overview page, find these two values. Each has a small "copy to clipboard" icon next to it — use that, don't try to select and copy the text manually.</p>
        <ul style={ulStyle}>
          <li><strong>Application (client) ID</strong> — looks like <code style={codeStyle}>11111111-2222-3333-4444-555555555555</code>. <em>This is what CIMA calls "App Client ID".</em></li>
          <li><strong>Directory (tenant) ID</strong> — looks similar. <em>This is what CIMA calls "Tenant ID".</em></li>
        </ul>
        <p style={{ fontSize: 12.5, color: 'var(--muted)' }}>
          Paste both into a temporary text document (Notepad, Notes, etc.) — you'll need them in step 8.
        </p>
      </Section>

      <Section id="secret" num="4" title="Create a client secret (in Azure Portal)">
        <WhereBadge tool="azure" />
        <p>Still on your CIMA app's page in Azure Portal:</p>
        <ol style={olStyle}>
          <li>Look at the <strong>left-hand sidebar</strong> of the page (the one specific to this app, not the main Azure sidebar). Find the section labelled <strong>"Manage"</strong>. Under "Manage", click <strong>Certificates &amp; secrets</strong>.</li>
          <li>On the Certificates &amp; secrets page, make sure you're on the <strong>Client secrets</strong> tab (it's the second of three tabs — Certificates / Client secrets / Federated credentials).</li>
          <li>Click the <strong>+ New client secret</strong> button.</li>
          <li>A right-side panel slides out. Fill in:
            <ul style={{ ...ulStyle, marginTop: 6 }}>
              <li><strong>Description</strong>: <code style={codeStyle}>CIMA integration</code></li>
              <li><strong>Expires</strong>: <strong>24 months</strong> (or follow your org's secret-rotation policy).</li>
            </ul>
          </li>
          <li>Click <strong>Add</strong> at the bottom of the panel.</li>
          <li><Callout>
            <strong>Critical — do this RIGHT NOW:</strong> the new secret will appear in the list with two columns: <strong>"Value"</strong> and <strong>"Secret ID"</strong>. Click the copy icon next to the <strong>Value</strong> column (the one that looks like a long random string). <em>The moment you navigate away from this page or refresh, Azure hides the Value forever and you'll have to generate a new secret.</em>
            <br/><br/>
            <strong>Do NOT copy the "Secret ID" — that's a different value and pasting it into CIMA won't work.</strong>
          </Callout></li>
          <li>Paste the secret Value into your temporary text document, next to the Tenant ID and App Client ID from step 3.</li>
          <li>Also note the <strong>Expires</strong> date shown in the list — you'll enter this in CIMA so we can email you 30 days before it auto-rotates.</li>
        </ol>
      </Section>

      <Section id="perms" num="5" title="Add API permissions + grant admin consent (in Azure Portal)">
        <WhereBadge tool="azure" />
        <p>Still on your CIMA app's page in Azure Portal:</p>
        <ol style={olStyle}>
          <li>In the same <strong>left-hand sidebar under "Manage"</strong>, click <strong>API permissions</strong> (it's right below Certificates &amp; secrets).</li>
          <li>You'll see a default permission already listed: <code style={codeStyle}>User.Read</code> (Delegated). <em>Leave it — we don't use it but removing it is unnecessary.</em></li>
          <li>Click the <strong>+ Add a permission</strong> button.</li>
          <li>A panel slides out. Click the big <strong>Microsoft Graph</strong> tile at the top.</li>
          <li>On the next screen you'll see two large buttons: <strong>Delegated permissions</strong> and <strong>Application permissions</strong>. Click <strong>Application permissions</strong> (the right one). <em>This is critical — CIMA acts as the app itself, not on behalf of a signed-in user.</em></li>
          <li>A long list appears, grouped by permission family. Use the <strong>search box</strong> at the top to find each permission below by typing its name, then tick the checkbox next to it. After ticking, the search resets — re-search for the next one. Repeat for all five.</li>
        </ol>
        <div style={{ background: 'var(--surface)', border: '1px solid var(--border-md)', borderRadius: 8, padding: '12px 16px', margin: '10px 0' }}>
          <PermRow code="User.Read.All"                    desc="Resolve the organiser user from their email" required />
          <PermRow code="OnlineMeetings.ReadWrite.All"     desc="Create Teams meetings on behalf of the organiser" required />
          <PermRow code="Calendars.ReadWrite"              desc="Send the calendar invite to attendees" required />
          <PermRow code="OnlineMeetingTranscript.Read.All" desc="Pull meeting transcripts (requires Teams Premium on organiser)" />
          <PermRow code="Chat.Read.All"                    desc="Fallback: read meeting chat if transcript isn't available" />
        </div>
        <ol style={{ ...olStyle, marginTop: 10 }} start={7}>
          <li>Click the blue <strong>Add permissions</strong> button at the bottom of the panel. The panel closes.</li>
          <li>You'll now see all 5 permissions in the list, each with the warning text "Not granted for [your tenant]" in the <strong>Status</strong> column — a yellow triangle.</li>
        </ol>
        <Callout>
          <strong>The critical step everyone misses</strong> — once the permissions are added, you MUST grant admin consent or none of them work:
          <ol style={{ ...olStyle, marginTop: 6 }}>
            <li>Look at the top of the API permissions list — there's a button labelled <strong>"Grant admin consent for [your tenant name]"</strong>.</li>
            <li>Click it. A confirmation dialog appears. Click <strong>Yes</strong>.</li>
            <li>The Status column for every permission should flip from a yellow triangle to a <strong>green tick + "Granted for [tenant]"</strong>.</li>
          </ol>
          <strong>Only Global Administrators see this button.</strong> If you don't see it, you're not signed in as a Global Admin — log out, log back in as one, and retry.
        </Callout>
      </Section>

      <Section id="policy" num="6" title="Restrict to one user via PowerShell (on your laptop)">
        <WhereBadge tool="powershell" />
        <p>This step is <strong>not in Azure Portal</strong> — it's in PowerShell on your own computer (Windows: Start menu → "PowerShell"; Mac: Terminal with PowerShell installed via <code style={codeStyle}>brew install powershell</code>).</p>
        <p>By default, the <code style={codeStyle}>OnlineMeetings.ReadWrite.All</code> permission you granted in step 5 lets the CIMA app create meetings on behalf of <em>any</em> user in your tenant. Your security team almost certainly wants this restricted to just the organiser account.</p>
        <p>Open PowerShell as an administrator. Copy and paste the commands below ONE AT A TIME (the backticks at the end of lines are PowerShell line continuations — keep them).</p>
        <p style={{ marginTop: 12, marginBottom: 4, fontWeight: 600 }}>(a) Install the Teams PowerShell module (only the first time on this laptop):</p>
        <pre style={codeBlock}>{`Install-Module MicrosoftTeams -Force
Connect-MicrosoftTeams`}</pre>
        <p style={{ marginTop: 4, fontSize: 12.5, color: 'var(--muted)' }}>
          <code style={codeStyle}>Connect-MicrosoftTeams</code> pops up a Microsoft sign-in window. Sign in with the SAME Global Admin account you used in Azure Portal.
        </p>
        <p style={{ marginTop: 12, marginBottom: 4, fontWeight: 600 }}>(b) Create the access policy. <strong>Replace <code style={codeStyle}>&lt;APP_CLIENT_ID_FROM_STEP_3&gt;</code> with the actual App Client ID you copied in step 3:</strong></p>
        <pre style={codeBlock}>{`New-CsApplicationAccessPolicy \`
  -Identity "CIMA-Policy" \`
  -AppIds "<APP_CLIENT_ID_FROM_STEP_3>" \`
  -Description "CIMA Crisis Management"`}</pre>
        <p style={{ marginTop: 12, marginBottom: 4, fontWeight: 600 }}>(c) Grant the policy to the organiser user only. <strong>Replace <code style={codeStyle}>cima-bot@yourcompany.com.au</code> with your actual organiser email:</strong></p>
        <pre style={codeBlock}>{`Grant-CsApplicationAccessPolicy \`
  -PolicyName "CIMA-Policy" \`
  -Identity "cima-bot@yourcompany.com.au"`}</pre>
        <p style={{ fontSize: 12.5, color: 'var(--muted)' }}>
          Each command should return without error. If you see <code style={codeStyle}>Policy "CIMA-Policy" already exists</code> on the second command, that's fine — it means you've run this before; skip to (c). The policy change can take up to <strong>10 minutes</strong> to propagate through Microsoft's systems, so if step 9's verification fails on <code style={codeStyle}>OnlineMeetings.ReadWrite.All</code> immediately, wait and try again.
        </p>
      </Section>

      <Section id="premium" num="7" title="(Recommended) Assign Teams Premium licence (in M365 Admin Center)">
        <WhereBadge tool="m365" />
        <p>This step is in a <strong>different portal</strong> from Azure — open a new tab to the Microsoft 365 Admin Center.</p>
        <ol style={olStyle}>
          <li>Go to <ExtLink href="https://admin.microsoft.com">admin.microsoft.com</ExtLink>. Sign in as Global Admin if not already.</li>
          <li>In the left sidebar, click <strong>Users → Active users</strong>.</li>
          <li>In the user list, find and click the row for the organiser account (e.g. <code style={codeStyle}>cima-bot@yourcompany.com.au</code>).</li>
          <li>A right-side panel slides out with the user's details. Click the <strong>Licenses and apps</strong> tab (near the top of that panel).</li>
          <li>Tick the checkbox for <strong>Microsoft Teams Premium</strong> (or whichever plan your org has that includes it — sometimes it's bundled as "Microsoft 365 E5" or "Teams Premium Introductory Pricing").</li>
          <li>Click <strong>Save changes</strong> at the bottom of the panel.</li>
        </ol>
        <p style={{ fontSize: 12.5, color: 'var(--muted)' }}>
          <strong>Without Teams Premium:</strong> meetings still get scheduled and attendees still receive invites — only the "Pull transcript" feature degrades, falling back to reading meeting chat. If your org doesn't have Teams Premium and won't buy it, just skip this step; everything else still works.
        </p>
      </Section>

      <Section id="paste" num="8" title="Paste credentials into CIMA (in this app)">
        <WhereBadge tool="cima" />
        <p>Back in <strong>CIMA</strong> (the app you're using right now):</p>
        <ol style={olStyle}>
          <li>Close this guide tab — or just leave it open and switch tabs. Go back to the CIMA tab where you started.</li>
          <li>In CIMA, click the cog icon at the top right → <strong>Settings</strong>. Then click <strong>Integrations</strong> in the Settings sidebar.</li>
          <li>Find the Microsoft Teams card and click <strong>Set up Microsoft Teams</strong> (or <strong>Replace credentials</strong> if a previous attempt exists).</li>
          <li>A form appears. Copy-paste each value from your temporary text document:
            <ul style={{ ...ulStyle, marginTop: 6 }}>
              <li><strong>Tenant ID</strong> (Directory (tenant) ID from step 3)</li>
              <li><strong>App Client ID</strong> (Application (client) ID from step 3)</li>
              <li><strong>App Client Secret</strong> — the <em>Value</em> from step 4. <strong>Double-check you copied the Value, not the Secret ID — they look similar but the Secret ID won't authenticate.</strong></li>
              <li><strong>Organiser email</strong> — e.g. <code style={codeStyle}>cima-bot@yourcompany.com.au</code>. Must match the user you granted the policy to in step 6(c).</li>
              <li><strong>Secret expiry</strong> — the "Expires" date from step 4 (optional but recommended; we email you 30 days before it expires).</li>
            </ul>
          </li>
          <li>Click <strong>Save &amp; verify</strong>. CIMA will probe Microsoft Graph in real time and report each permission's status — that's what you read in step 9.</li>
        </ol>
        <p style={{ fontSize: 12.5, color: 'var(--muted)' }}>
          After saving, you can safely delete the temporary text document — CIMA stores the secret encrypted in AWS Secrets Manager. We never display the secret back to you in plain text.
        </p>
      </Section>

      <Section id="verify" num="9" title="Read the verification result">
        <p>CIMA will probe Microsoft Graph with the credentials you just saved and report each permission's status:</p>
        <ul style={ulStyle}>
          <li><strong>✓ Granted</strong> — works.</li>
          <li><strong>Consent missing</strong> — you forgot to click the "Grant admin consent" button in step 5. Go back, click it, then re-verify.</li>
          <li><strong>Not granted</strong> — the permission isn't on the list. Add it in step 5 then re-verify.</li>
          <li><strong>Auth failed</strong> — the Tenant ID, Client ID, or Secret is wrong. Step 10 below.</li>
        </ul>
        <p>Once every <strong>required</strong> permission shows ✓ Granted, you're done. The optional permissions (transcripts, chat) can stay missing without blocking the rest of CIMA — they just degrade transcript-pulling features.</p>
      </Section>

      <Section id="errors" num="10" title="Common errors and fixes">
        <ErrorRow err="AADSTS700016: Application not found in the directory"
                  fix="Tenant ID or App Client ID is wrong. Double-check step 3." />
        <ErrorRow err="AADSTS7000215: Invalid client secret provided"
                  fix="The secret was typo'd, expired, or you copied the Secret ID instead of the Value. Generate a new one (step 4) and re-save." />
        <ErrorRow err="ResourceNotFound: User not found"
                  fix="The organiser email doesn't exist in this tenant. Confirm in the Microsoft 365 admin center → Active users." />
        <ErrorRow err="Forbidden: Authorization_RequestDenied (on a specific permission)"
                  fix="Admin consent wasn't granted for that permission. Go to API permissions → click 'Grant admin consent for [your tenant]'." />
        <ErrorRow err="Transcripts return empty"
                  fix="The organiser is missing a Teams Premium license. Step 7 fixes it." />
      </Section>

      <div style={{ marginTop: 32, padding: 18, background: 'var(--surface)', border: '1px solid var(--border-md)', borderRadius: 10 }}>
        <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 4 }}>Still stuck?</div>
        <div style={{ fontSize: 13, color: 'var(--muted)' }}>
          Email <a href="mailto:support@enigmaglobal.com.au" style={{ color: 'var(--primary)', fontWeight: 600 }}>support@enigmaglobal.com.au</a> with the verification result and any error messages, and we'll help you finish the setup.
        </div>
      </div>
    </div>
  );
}

// ── Sub-components ──────────────────────────────────────────────────────

function Section({ id, num, title, children }: { id: string; num: string; title: string; children: React.ReactNode }) {
  return (
    <section id={id} style={{ marginBottom: 36, scrollMarginTop: 80 }}>
      <h2 style={{ fontSize: 19, fontWeight: 800, margin: '0 0 12px', letterSpacing: '-0.01em', display: 'flex', alignItems: 'center', gap: 12 }}>
        <span style={{
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          width: 30, height: 30, borderRadius: 9,
          background: 'var(--primary)',
          color: 'white', fontSize: 13, fontWeight: 800,
        }}>{num}</span>
        {title}
      </h2>
      <div>{children}</div>
    </section>
  );
}

function PermRow({ code, desc, required }: { code: string; desc: string; required?: boolean }) {
  return (
    <div style={{ display: 'flex', gap: 12, padding: '6px 0', alignItems: 'baseline' }}>
      <code style={{ ...codeStyle, minWidth: 220 }}>{code}</code>
      <span style={{ fontSize: 12.5, color: 'var(--fg-soft)' }}>{desc}</span>
      {required && <span style={{ marginLeft: 'auto', fontSize: 9.5, fontWeight: 800, color: '#B91C1C', letterSpacing: 0.4, textTransform: 'uppercase' }}>Required</span>}
    </div>
  );
}

function ErrorRow({ err, fix }: { err: string; fix: string }) {
  return (
    <div style={{ marginBottom: 12, padding: 12, background: 'var(--surface)', border: '1px solid var(--border-md)', borderRadius: 8 }}>
      <div style={{ fontFamily: 'var(--font-mono, monospace)', fontSize: 12, color: '#B91C1C', marginBottom: 4 }}>{err}</div>
      <div style={{ fontSize: 13, color: 'var(--fg-soft)' }}>{fix}</div>
    </div>
  );
}

function Callout({ children }: { children: React.ReactNode }) {
  return (
    <div style={{
      background: 'rgba(245,158,11,0.08)',
      border: '1px solid rgba(245,158,11,0.32)',
      borderLeft: '3px solid #F59E0B',
      borderRadius: 8,
      padding: '10px 14px',
      margin: '12px 0',
      fontSize: 13,
      color: 'var(--fg-soft)',
    }}>{children}</div>
  );
}

function ExtLink({ href, children }: { href: string; children: React.ReactNode }) {
  return (
    <a href={href} target="_blank" rel="noopener noreferrer"
       style={{ color: 'var(--primary)', fontWeight: 600 }}>
      {children} ↗
    </a>
  );
}

/** Sticky "you should be doing this here" badge at the top of each step.
 *  Yash flagged that admins couldn't tell which portal each click was in —
 *  the badge makes the location unambiguous before they read the steps. */
function WhereBadge({ tool }: { tool: 'azure' | 'm365' | 'powershell' | 'cima' | 'checklist' }) {
  const config = {
    azure:      { icon: '🔵', label: 'Do this in: Azure Portal',                 sub: 'portal.azure.com',     colour: '#0078D4' },
    m365:       { icon: '🟢', label: 'Do this in: Microsoft 365 Admin Center',   sub: 'admin.microsoft.com',  colour: '#22C55E' },
    powershell: { icon: '⚫', label: 'Do this in: PowerShell on your laptop',    sub: 'Not in any browser',   colour: '#475569' },
    cima:       { icon: '🔵', label: 'Do this in: CIMA (this app)',              sub: 'cima.enigmaglobal.ai', colour: '#0067FE' },
    checklist:  { icon: '📋', label: 'Setup checklist',                          sub: 'Read this first',      colour: '#94A3B8' },
  }[tool];
  return (
    <div style={{
      display: 'inline-flex', alignItems: 'center', gap: 10,
      background: 'var(--surface)', border: `1px solid ${config.colour}33`,
      borderLeft: `3px solid ${config.colour}`,
      borderRadius: 6, padding: '6px 12px',
      fontSize: 12, fontWeight: 600,
      marginBottom: 12,
    }}>
      <span style={{ fontSize: 14 }}>{config.icon}</span>
      <span style={{ color: 'var(--fg)' }}>{config.label}</span>
      <span style={{ color: 'var(--muted)', fontWeight: 400, fontSize: 11.5 }}>· {config.sub}</span>
    </div>
  );
}

/** Tool row in the prereq checklist — shows each portal/tool with what
 *  it's for, so admins can open all four tabs upfront. */
function ToolRow({ icon, name, url, purpose }: { icon: string; name: string; url: string | null; purpose: string }) {
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '6px 0' }}>
      <span style={{ fontSize: 14, lineHeight: 1.4, flex: '0 0 auto' }}>{icon}</span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 600, fontSize: 13 }}>
          {url
            ? <ExtLink href={url}>{name}</ExtLink>
            : <span>{name}</span>}
        </div>
        <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 2 }}>{purpose}</div>
      </div>
    </div>
  );
}

// ── Styles ──
const tocStyle: React.CSSProperties = {
  background: 'var(--surface)', border: '1px solid var(--border-md)',
  borderRadius: 10, padding: '12px 16px', marginBottom: 28,
  display: 'flex', flexDirection: 'column', gap: 4,
};
const tocLink: React.CSSProperties = {
  display: 'block', padding: '4px 0', fontSize: 13, color: 'var(--fg-soft)',
  textDecoration: 'none',
};
const codeStyle: React.CSSProperties = {
  fontFamily: 'var(--font-mono, monospace)', fontSize: 12.5,
  background: 'rgba(99,55,138,0.10)', color: 'var(--primary)',
  padding: '1px 7px', borderRadius: 4,
};
const codeBlock: React.CSSProperties = {
  background: 'var(--surface)', border: '1px solid var(--border-md)',
  borderRadius: 8, padding: '14px 16px', fontSize: 12.5,
  fontFamily: 'var(--font-mono, monospace)', lineHeight: 1.6,
  overflowX: 'auto', whiteSpace: 'pre',
};
const ulStyle: React.CSSProperties = { paddingLeft: 22, margin: '8px 0' };
const olStyle: React.CSSProperties = { paddingLeft: 22, margin: '8px 0' };
